/**
 * Placeholder click-to-edit popup (DOCX-native template editor).
 *
 * Clicking a placeholder in a template opens a small styled balloon anchored at
 * the placeholder to set its Name + Required flag — PropDocs' inline placeholder
 * authoring, replacing the old window.prompt.
 *
 * Detection: the editor's ProseMirror is hidden and the canvas is custom-painted,
 * so there's no per-placeholder DOM node to attach a click listener to. We poll
 * the live PM selection (250ms) and, when the caret lands inside a placeholder
 * SDT, anchor the balloon at the painted caret rect ([data-testid="caret"]) —
 * the same anchor the editor's own floating comment button uses.
 *
 * Ported from the standalone prototype's PlaceholderPopup (template-mode only;
 * the deal-field fill UI is a deal-document concern for a later phase).
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { DocxEditorRef } from '@propdocs-editor/react';
import type { EditorView } from 'prosemirror-view';

const PLACEHOLDER_TAG = 'propdocs:placeholder@1';
// PropDocs encodes "required" as the sdt `lock` attr (there is no `required`
// attr on the node). MUST match buildPlaceholderNode in the editor package.
const REQUIRED_LOCK = 'propdocs:required';

// After INSERTING a placeholder (toolbar/"+"), the caret is inside it but no
// editor CLICK occurred, so the click-gated popup wouldn't open. The insert path
// calls this to explicitly request the popup open for naming.
const openRequestListeners = new Set<() => void>();
export function requestPlaceholderPopupOpen(): void {
  openRequestListeners.forEach((l) => l());
}

interface ActivePlaceholder {
  pos: number;
  name: string;
  content: string;
  required: boolean;
}

function getView(editorRef: React.RefObject<DocxEditorRef | null>): EditorView | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (editorRef.current?.getEditorRef?.() as any)?.getView?.() ?? null;
}

/** The placeholder SDT enclosing position `p`, if any. */
function placeholderAt(view: EditorView, p: number): ActivePlaceholder | null {
  let found: ActivePlaceholder | null = null;
  view.state.doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name === 'sdt' && node.attrs.tag === PLACEHOLDER_TAG) {
      const start = pos;
      const end = pos + node.nodeSize;
      if (p >= start && p <= end) {
        const name = String(node.attrs.placeholder ?? node.attrs.alias ?? 'Field');
        found = {
          pos,
          name,
          content: node.textContent,
          required: node.attrs.lock === REQUIRED_LOCK,
        };
        return false;
      }
    }
    return true;
  });
  return found;
}

/** Set the placeholder's name (alias/placeholder attr) + required flag, and
 *  re-render its unfilled label to [newName] if it wasn't filled. */
function setNameRequired(
  view: EditorView,
  ph: ActivePlaceholder,
  name: string,
  required: boolean
): void {
  const node = view.state.doc.nodeAt(ph.pos);
  if (!node || node.type.name !== 'sdt') return;
  const wasUnfilled = ph.content === `[${ph.name}]`;
  let tr = view.state.tr.setNodeMarkup(ph.pos, undefined, {
    ...node.attrs,
    placeholder: name,
    alias: name,
    lock: required ? REQUIRED_LOCK : null,
  });
  if (wasUnfilled) {
    const start = ph.pos + 1;
    const end = ph.pos + node.nodeSize - 1;
    tr = tr.replaceWith(start, end, view.state.schema.text(`[${name}]`));
  }
  view.dispatch(tr);
}

export function PlaceholderPopup({
  editorRef,
  onEdited,
}: {
  editorRef: React.RefObject<DocxEditorRef | null>;
  /** Fired after a placeholder's name/required is applied (→ autosave). */
  onEdited?: () => void;
}) {
  const [active, setActive] = useState<ActivePlaceholder | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const interactingRef = useRef(false);
  const lastPosRef = useRef<number | null>(null);

  const popupRef = useRef<HTMLDivElement>(null);

  // Position the popup relative to the field's rect (viewport coords, since the
  // popup is position:fixed). Prefer BELOW the field; flip ABOVE when there's
  // not enough room below (field near the bottom of the screen). Clamp left/top
  // so the popup never spills off any edge.
  const anchorToRect = useCallback((r: DOMRect): { top: number; left: number } => {
    const GAP = 6;
    const MARGIN = 8; // keep this far from the viewport edges
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Measure the live popup if mounted, else fall back to sensible estimates.
    const el = popupRef.current;
    const popupH = el?.offsetHeight || 200;
    const popupW = el?.offsetWidth || 300;

    const roomBelow = vh - r.bottom;
    const flipAbove = roomBelow < popupH + GAP + MARGIN && r.top > roomBelow;

    let top = flipAbove ? r.top - popupH - GAP : r.bottom + GAP;
    let left = r.left;

    // Clamp within the viewport.
    top = Math.max(MARGIN, Math.min(top, vh - popupH - MARGIN));
    left = Math.max(MARGIN, Math.min(left, vw - popupW - MARGIN));
    return { top, left };
  }, []);

  const anchorToCaret = useCallback((): { top: number; left: number } | null => {
    const caret = document.querySelector<HTMLElement>('[data-testid="caret"]');
    if (caret) {
      const r = caret.getBoundingClientRect();
      if (r.width || r.height || r.top) return anchorToRect(r);
    }
    const rect = document.querySelector<HTMLElement>(
      '[data-testid="selection-overlay"] [data-testid^="selection-rect-"]'
    );
    if (rect) return anchorToRect(rect.getBoundingClientRect());
    return null;
  }, [anchorToRect]);

  // Open ONLY when the user actively clicks a placeholder — not merely because
  // the caret is resting inside one (which would reopen the popup right after
  // the user closes it). We detect a click via mousedown on the editor surface,
  // then read the placeholder at the resulting selection. A `dismissedPos`
  // guard also prevents reopening for a placeholder the user just closed until
  // the selection leaves it.
  const dismissedPosRef = useRef<number | null>(null);
  const openRequestedRef = useRef(false);

  // A mousedown inside the editor content requests an open on the NEXT poll
  // (after PM has updated the selection to the click point).
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // Ignore clicks on our own popup (its onMouseDown sets interacting).
      if (target?.closest('[data-placeholder-popup]')) return;
      // Only clicks within the painted editor page area count.
      if (target?.closest('[class*="layout-page"], [class*="paged-editor"], [data-testid="caret"]')) {
        openRequestedRef.current = true;
      }
    };
    document.addEventListener('mousedown', onDown, true);
    // Explicit open request from the insert path (no editor click occurred).
    const onInsertRequest = () => {
      dismissedPosRef.current = null; // allow reopen even if just dismissed
      openRequestedRef.current = true;
    };
    openRequestListeners.add(onInsertRequest);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      openRequestListeners.delete(onInsertRequest);
    };
  }, []);

  // Poll: keep the anchor fresh while open, close when the caret leaves the
  // placeholder, and open ONLY when a click was requested over a placeholder.
  useEffect(() => {
    let timer = 0;
    const tick = () => {
      if (!interactingRef.current) {
        const view = getView(editorRef);
        if (view) {
          const { from } = view.state.selection;
          const ph = placeholderAt(view, from);

          if (!ph) {
            // Caret left any placeholder → clear dismissed guard + close.
            dismissedPosRef.current = null;
            if (lastPosRef.current !== null) {
              lastPosRef.current = null;
              setActive(null);
              setAnchor(null);
            }
          } else if (openRequestedRef.current && ph.pos !== dismissedPosRef.current) {
            // A real click landed in a placeholder we haven't just dismissed.
            openRequestedRef.current = false;
            lastPosRef.current = ph.pos;
            setActive(ph);
            setAnchor(anchorToCaret());
          } else if (lastPosRef.current === ph.pos) {
            // Already open on this placeholder → keep its data AND anchor fresh
            // (so the popup follows the field as the page scrolls).
            setActive((prev) => (prev && prev.pos === ph.pos ? { ...ph } : prev));
            const next = anchorToCaret();
            if (next) setAnchor((prev) => (prev && prev.top === next.top && prev.left === next.left ? prev : next));
          }
          // Otherwise: caret is in a placeholder but no click / it was dismissed
          // → stay closed.
        }
        openRequestedRef.current = false;
      }
      timer = window.setTimeout(tick, 200);
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [editorRef, anchorToCaret]);

  // Once the popup has actually mounted (and thus has a real measured height),
  // re-anchor so the flip-above/clamp math uses the true size instead of the
  // 200px estimate — important when opening near the bottom of the screen.
  useLayoutEffect(() => {
    if (!active) return;
    const next = anchorToCaret();
    if (next) setAnchor((prev) => (prev && prev.top === next.top && prev.left === next.left ? prev : next));
    // Depend on active.pos so this fires per placeholder open, after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.pos]);

  // While open, re-anchor immediately on scroll/resize so the popup stays glued
  // to the field (the 200ms poll alone lags visibly during a scroll). Capture
  // phase catches scrolls on any inner scroll container, not just window.
  useEffect(() => {
    if (!active) return;
    const reposition = () => {
      const next = anchorToCaret();
      if (next) setAnchor(next);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [active, anchorToCaret]);

  const close = useCallback(() => {
    interactingRef.current = false;
    // Remember the placeholder we're closing so the poll won't reopen it while
    // the caret is still inside; it reopens only on a fresh click.
    dismissedPosRef.current = lastPosRef.current;
    lastPosRef.current = null;
    setActive(null);
    setAnchor(null);
    getView(editorRef)?.focus();
  }, [editorRef]);

  if (!active || !anchor) return null;

  return (
    <div
      ref={popupRef}
      data-placeholder-popup
      style={{ ...POPUP, top: anchor.top, left: anchor.left }}
      onMouseDown={() => {
        interactingRef.current = true;
      }}
    >
      <TemplateBalloon
        key={active.pos}
        active={active}
        onApply={(name, required) => {
          const view = getView(editorRef);
          if (view) setNameRequired(view, active, name, required);
          close();
          onEdited?.();
        }}
        onClose={close}
      />
    </div>
  );
}

function TemplateBalloon({
  active,
  onApply,
  onClose,
}: {
  active: ActivePlaceholder;
  onApply: (name: string, required: boolean) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(active.name);
  const [required, setRequired] = useState(active.required);
  const [focused, setFocused] = useState(false);
  const apply = () => onApply(name.trim() || active.name, required);
  return (
    <div>
      <div style={HEAD}>
        <strong style={{ fontSize: 13 }}>Placeholder</strong>
        <button style={X} onClick={onClose} title="Close" type="button">
          ✕
        </button>
      </div>
      <label style={LABEL}>Name</label>
      <input
        style={{
          ...INPUT,
          // PropDocs accent focus ring (was the browser default blue).
          borderColor: focused ? ACCENT : '#dadce0',
          boxShadow: focused ? `0 0 0 2px rgba(0,108,144,0.18)` : 'none',
        }}
        autoFocus
        value={name}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply();
          if (e.key === 'Escape') onClose();
        }}
      />
      {/* Custom styled checkbox row. A single onClick toggles — NOT wrapped in a
          <label>+hidden-input (that double-fired: label click + input onChange
          cancelled out, so the box never changed). */}
      <div
        style={CHECKROW}
        role="checkbox"
        aria-checked={required}
        tabIndex={0}
        onClick={() => setRequired((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            setRequired((v) => !v);
          }
        }}
      >
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            border: `1.5px solid ${required ? ACCENT : '#c0c6cf'}`,
            background: required ? ACCENT : '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.12s, border-color 0.12s',
          }}
        >
          {required && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
        <span>Required</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 12 }}>
        <button style={BTN} onClick={onClose} type="button">
          Cancel
        </button>
        <button style={BTN_PRIMARY} onClick={apply} type="button">
          Apply
        </button>
      </div>
    </div>
  );
}

// ── styles (PropDocs #006c90 accent, Arial to match the app) ─────────────────
const ACCENT = '#006c90';
const POPUP: React.CSSProperties = {
  position: 'fixed',
  width: 280,
  background: '#fff',
  color: '#202124',
  border: '1px solid #dadce0',
  borderRadius: 10,
  boxShadow: '0 10px 32px rgba(0,0,0,.22)',
  padding: 14,
  zIndex: 5000,
  fontFamily: 'Arial, sans-serif',
};
const HEAD: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 8,
  gap: 8,
};
const X: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 13,
  color: '#5f6368',
  lineHeight: 1,
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: '#5f6368',
  marginBottom: 3,
  fontWeight: 600,
};
const INPUT: React.CSSProperties = {
  width: '100%',
  height: 32,
  fontSize: 13,
  fontFamily: 'Arial, sans-serif',
  padding: '0 8px',
  boxSizing: 'border-box',
  border: '1px solid #dadce0',
  borderRadius: 6,
  outline: 'none',
  transition: 'border-color 0.12s, box-shadow 0.12s',
};
const CHECKROW: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  marginTop: 10,
  cursor: 'pointer',
  userSelect: 'none',
};
const BTN: React.CSSProperties = {
  height: 32,
  fontSize: 13,
  fontFamily: 'Arial, sans-serif',
  padding: '0 14px',
  cursor: 'pointer',
  border: '1px solid #dadce0',
  borderRadius: 6,
  background: '#fff',
  color: '#202124',
};
const BTN_PRIMARY: React.CSSProperties = {
  ...BTN,
  background: ACCENT,
  color: '#fff',
  border: `1px solid ${ACCENT}`,
};
